import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  TrendingUp,
  MousePointerClick,
  Eye,
  Euro,
  Target,
  Activity,
  AlertTriangle,
  Settings,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import GoogleAdsLinkDialog from '../../components/sea/GoogleAdsLinkDialog';
import CRMRoiWidget from '../../components/sea/CRMRoiWidget';
import SetupBanner from '../../components/SetupBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIOD_OPTIONS = [
  { value: 'LAST_7_DAYS', label: '7 derniers jours' },
  { value: 'LAST_14_DAYS', label: '14 derniers jours' },
  { value: 'LAST_30_DAYS', label: '30 derniers jours' },
  { value: 'THIS_MONTH', label: 'Ce mois' },
  { value: 'LAST_MONTH', label: 'Mois dernier' },
  { value: 'LAST_90_DAYS', label: '90 derniers jours' },
];

function periodToDates(period) {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const sub = (n) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  switch (period) {
    case 'LAST_7_DAYS': return [fmt(sub(7)), fmt(sub(1))];
    case 'LAST_14_DAYS': return [fmt(sub(14)), fmt(sub(1))];
    case 'LAST_30_DAYS': return [fmt(sub(30)), fmt(sub(1))];
    case 'LAST_90_DAYS': return [fmt(sub(90)), fmt(sub(1))];
    case 'THIS_MONTH': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return [fmt(start), fmt(today)];
    }
    case 'LAST_MONTH': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return [fmt(start), fmt(end)];
    }
    default: return [fmt(sub(30)), fmt(sub(1))];
  }
}

function formatEUR(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
}
function formatNum(n) {
  return new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
}
function formatPct(n) {
  return `${((n || 0) * 100).toFixed(2)}%`;
}

export default function SEADashboard() {
  const [period, setPeriod] = useState('LAST_30_DAYS');
  const [config, setConfig] = useState(null);
  const [globalData, setGlobalData] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [loading, setLoading] = useState(true);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  const [start, end] = useMemo(() => periodToDates(period), [period]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const cfg = await axios.get(`${API}/google-ads/config`, { withCredentials: true });
        if (cancel) return;
        setConfig(cfg.data);
      } catch (e) {
        if (!cancel) setConfig({ is_connected: false });
      }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    if (!config?.is_connected) { setLoading(false); return; }
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ start, end });
        const { data } = await axios.get(`${API}/google-ads/dashboard/global?${params}`, { withCredentials: true });
        if (cancel) return;
        setGlobalData(data);
      } catch (e) {
        if (!cancel) toast.error(e.response?.data?.detail || 'Erreur de chargement Google Ads');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [config?.is_connected, start, end]);

  // Fetch daily series for the selected client (or skip if 'all' — we don't have a multi-client daily endpoint)
  useEffect(() => {
    if (!config?.is_connected || selectedClient === 'all') { setDailyData([]); return; }
    let cancel = false;
    (async () => {
      try {
        const params = new URLSearchParams({ client_id: selectedClient, start, end });
        const { data } = await axios.get(`${API}/google-ads/daily?${params}`, { withCredentials: true });
        if (!cancel) setDailyData(data.days || []);
      } catch (e) {
        if (!cancel) setDailyData([]);
      }
    })();
    return () => { cancel = true; };
  }, [config?.is_connected, selectedClient, start, end]);

  const totals = globalData?.totals || {};
  const perClient = globalData?.per_client || [];
  const errors = globalData?.errors || [];
  const filteredClient = selectedClient === 'all' ? null : perClient.find(c => c.client_id === selectedClient);
  const displayedTotals = filteredClient || totals;

  if (!config) {
    return <Layout><div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div></Layout>;
  }

  if (!config.is_connected) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Tableau de bord SEA</h1>
            <p className="text-neutral-500 mt-1">Performance Google Ads multi-clients</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <h2 className="font-heading font-semibold text-lg text-neutral-900">Google Ads non connecté</h2>
                <p className="text-sm text-neutral-700 mt-1">
                  Pour afficher les données SEA, configurez votre compte Google Ads (MCC) dans les paramètres.
                </p>
                <Link to="/settings" className="inline-block mt-4">
                  <Button className="bg-orange-600 hover:bg-orange-700" data-testid="sea-goto-settings-btn">
                    <Settings className="w-4 h-4 mr-1" /> Configurer Google Ads
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="sea-dashboard">
        <SetupBanner />
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Tableau de bord SEA</h1>
            <p className="text-neutral-500 mt-1">
              Google Ads · MCC <span className="font-mono text-xs bg-neutral-100 px-1.5 py-0.5 rounded">{config.login_customer_id}</span>
              {config.connected_email && <> · {config.connected_email}</>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-44" data-testid="sea-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-56" data-testid="sea-client-select"><SelectValue placeholder="Tous les clients" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clients ({perClient.length})</SelectItem>
                {perClient.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.client_name}</SelectItem>)}
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={() => setLinkDialogOpen(true)} data-testid="sea-link-clients-btn">
              <ExternalLink className="w-4 h-4 mr-1" /> Lier comptes
            </Button>
          </div>
        </div>

        {/* No-clients state */}
        {!loading && perClient.length === 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
            <p className="text-sm text-blue-900">
              <strong>Aucun client lié à Google Ads pour le moment.</strong> Cliquez sur <em>Lier comptes</em> pour mapper vos clients AEGIS aux sous-comptes du MCC.
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard icon={Eye} label="Impressions" value={formatNum(displayedTotals.impressions)} testId="kpi-impressions" />
              <KpiCard icon={MousePointerClick} label="Clics" value={formatNum(displayedTotals.clicks)} sub={`CTR ${formatPct(displayedTotals.ctr)}`} testId="kpi-clicks" />
              <KpiCard icon={Euro} label="Coût" value={formatEUR(displayedTotals.cost)} sub={`CPC moy. ${formatEUR(displayedTotals.average_cpc)}`} testId="kpi-cost" />
              <KpiCard icon={Target} label="Conversions" value={formatNum(displayedTotals.conversions)} sub={displayedTotals.cost_per_conversion ? `CPA ${formatEUR(displayedTotals.cost_per_conversion)}` : '—'} testId="kpi-conversions" />
            </div>

            {/* ROAS card (only on global view) */}
            {selectedClient === 'all' && totals.roas !== undefined && (
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-orange-700 uppercase tracking-wide font-mono">ROAS global</p>
                    <p className="font-heading font-bold text-3xl text-neutral-900 mt-1">{(totals.roas || 0).toFixed(2)}x</p>
                    <p className="text-xs text-neutral-500 mt-0.5">Valeur conversions / Coût · {formatEUR(totals.conversions_value)}</p>
                  </div>
                  <TrendingUp className="w-12 h-12 text-orange-300" />
                </div>
              </div>
            )}

            {/* Real CRM ROI widget (closed-loop Sellsy + custom) */}
            {selectedClient === 'all' && <CRMRoiWidget days={30} />}

            {/* Daily evolution chart (per-client only) */}
            {selectedClient !== 'all' && dailyData.length > 0 && (
              <div className="bg-white border border-neutral-200 rounded-lg p-5">
                <h3 className="font-heading font-semibold text-base mb-4">Évolution journalière — {filteredClient?.client_name}</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={dailyData}>
                    <CartesianGrid stroke="#f3f4f6" strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#737373' }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#737373' }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#737373' }} />
                    <Tooltip contentStyle={{ backgroundColor: '#0A0A0A', border: 'none', borderRadius: 4, color: '#fff' }} />
                    <Line yAxisId="left" type="monotone" dataKey="cost" stroke="#EA580C" strokeWidth={2} name="Coût (€)" dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="conversions" stroke="#002FA7" strokeWidth={2} name="Conversions" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Per-client breakdown — stacked KPI cards (Lot 4: découpe par société) */}
            {selectedClient === 'all' && perClient.length > 0 && (
              <div className="space-y-4" data-testid="sea-per-client-breakdown">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-base">Performance par société</h3>
                  <Badge variant="outline" className="text-xs">{perClient.length} société(s)</Badge>
                </div>
                {perClient.map((c) => {
                  const roas = c.cost > 0 && c.conversions_value ? c.conversions_value / c.cost : null;
                  return (
                    <div
                      key={c.client_id}
                      className="bg-white border border-neutral-200 rounded-lg p-5"
                      data-testid={`sea-client-card-${c.client_id}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-neutral-100">
                        <div>
                          <h4 className="font-heading font-semibold text-lg text-neutral-900">{c.client_name}</h4>
                          <p className="text-xs text-neutral-500 font-mono">CID {c.google_ads_customer_id}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {roas !== null && (
                            <Badge className="bg-orange-50 text-orange-700 border border-orange-200 text-[11px]">
                              ROAS {roas.toFixed(2)}x
                            </Badge>
                          )}
                          <button
                            onClick={() => setSelectedClient(c.client_id)}
                            className="text-xs text-[#002FA7] hover:underline"
                            data-testid={`focus-${c.client_id}`}
                          >
                            Voir détail →
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                        <MiniKpi icon={Eye} label="Impressions" value={formatNum(c.impressions)} />
                        <MiniKpi icon={MousePointerClick} label="Clics" value={formatNum(c.clicks)} sub={`CTR ${formatPct(c.ctr)}`} />
                        <MiniKpi icon={Euro} label="Coût" value={formatEUR(c.cost)} sub={`CPC ${formatEUR(c.average_cpc)}`} />
                        <MiniKpi icon={Target} label="Conversions" value={formatNum(c.conversions)} sub={c.cost_per_conversion ? `CPA ${formatEUR(c.cost_per_conversion)}` : '—'} />
                        <MiniKpi icon={TrendingUp} label="Valeur conv." value={formatEUR(c.conversions_value)} sub={c.value_per_conversion ? `${formatEUR(c.value_per_conversion)} / conv.` : '—'} highlight />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Errors */}
            {errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
                <div className="flex items-center gap-2 text-red-800 font-medium mb-2">
                  <AlertTriangle className="w-4 h-4" /> Erreurs de récupération ({errors.length})
                </div>
                <ul className="space-y-1 text-red-700 text-xs">
                  {errors.slice(0, 5).map((e) => <li key={e.client_id || e.client_name}><strong>{e.client_name}</strong>: {e.error}</li>)}
                </ul>
              </div>
            )}
          </>
        )}

        <GoogleAdsLinkDialog
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
          onLinked={() => {
            // re-fetch global data after linking
            if (config?.is_connected) {
              const params = new URLSearchParams({ start, end });
              axios.get(`${API}/google-ads/dashboard/global?${params}`, { withCredentials: true })
                .then(({ data }) => setGlobalData(data));
            }
          }}
        />
      </div>
    </Layout>
  );
}

function KpiCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide font-mono text-neutral-500">{label}</p>
        <Icon className="w-4 h-4 text-neutral-400" />
      </div>
      <p className="font-heading font-bold text-2xl mt-2 text-neutral-900">{value}</p>
      {sub && <p className="text-xs text-neutral-500 mt-1">{sub}</p>}
    </div>
  );
}

function MiniKpi({ icon: Icon, label, value, sub, highlight }) {
  return (
    <div className={`rounded-md p-3 ${highlight ? 'bg-orange-50 border border-orange-200' : 'bg-neutral-50 border border-neutral-100'}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-mono text-neutral-500">
        <Icon className="w-3 h-3" />{label}
      </div>
      <p className={`font-heading font-bold text-lg mt-1 ${highlight ? 'text-orange-700' : 'text-neutral-900'}`}>{value}</p>
      {sub && <p className="text-[10px] text-neutral-500 mt-0.5">{sub}</p>}
    </div>
  );
}
